/**
 * Which API this POS should talk to, derived from where it is being served.
 *
 * One static bundle serves every tenant (SCOPE §12.3, decision 97): the same
 * files answer on `sitzmark.shekko.app`, `demo.shekko.app` and every future
 * `<slug>.shekko.app`, so the API's address cannot be a single compiled-in
 * value — it has to come from the hostname, the same place the tenant slug
 * comes from. The shape is the platform standard's (HOSTING.md "Domains"):
 * the tenant's API lives at `<slug>.api.shekko.app`, staging at
 * `<slug>.api.staging.shekko.app` — insert `api.` after the slug, keep the
 * rest.
 *
 * Everything that is not a `shekko.app` tenant hostname — localhost, the
 * browser suite, any other domain — falls back to the compiled
 * `VITE_API_URL`, exactly as before. The derivation is additive: nothing
 * behaves differently unless it is actually served from the product domain.
 */

/** The product's domain. A tenant hostname is `<slug>[.staging].shekko.app`. */
export const PRODUCT_DOMAIN = 'shekko.app';

export function apiOriginFor(hostname: string, compiledBase: string | undefined): string {
  const fallback = compiledBase ?? 'http://localhost:4000';

  const suffix = `.${PRODUCT_DOMAIN}`;
  if (!hostname.endsWith(suffix)) return fallback;

  // 'sitzmark' on production, 'sitzmark.staging' on staging.
  const front = hostname.slice(0, -suffix.length);
  const [slug, ...environment] = front.split('.');
  // The bare domain and `www` are the marketing surface, not a tenant.
  if (slug === undefined || slug === '' || slug === 'www') return fallback;

  return `https://${[slug, 'api', ...environment, PRODUCT_DOMAIN].join('.')}`;
}

/**
 * The store's web shop, from the same hostname (decision 157).
 *
 * The same shape as the API's: `sitzmark.shekko.app` → `sitzmark.shop.shekko.app`,
 * and staging's likewise. Anywhere else — a laptop, the browser suite — it is
 * the configured shop, `VITE_SHOP_URL`, or the shop's local port.
 */
export function shopOriginFor(hostname: string, compiledBase: string | undefined): string {
  const fallback = (compiledBase ?? 'http://localhost:3000').replace(/\/+$/, '');

  const suffix = `.${PRODUCT_DOMAIN}`;
  if (!hostname.endsWith(suffix)) return fallback;

  const front = hostname.slice(0, -suffix.length);
  const [slug, ...environment] = front.split('.');
  if (slug === undefined || slug === '' || slug === 'www') return fallback;

  return `https://${[slug, 'shop', ...environment, PRODUCT_DOMAIN].join('.')}`;
}
